<template>
 <div class="subcategory-page bg-white-pearl">
    <!-- Breadcrumbs -->
    <Breadcrumbs />

    <!-- Groups List -->
    <v-container class="subcategory-container">
      <v-row dense>
        <v-col v-for="group in groups" :key="group.id" cols="12">
          <v-card
            class="subcategory-card"
            outlined
            @click="navigateToSections(group)"
          >
            <div class="card-content">
              <span class="subcategory-name">{{ group.title }}</span>
              <v-icon icon="mdi-chevron-right" class="arrow-icon"></v-icon>
            </div>
          </v-card>
            <v-divider></v-divider>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import HeaderSUUQ from "@/components/HeaderSUUQ.vue";
import CategoryTitle from "@/components/CategoryTitle.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";

const store = useStore();
const router = useRouter();
const route = useRoute();

// Fetch groups from Vuex
const groups = computed(() => {
  const data = store.state.NewCategory.groups;
  console.log("Groups in component for job:", data); // Debugging log
  return data;
});

// Dynamically determine the subcategoryKey
const subcategoryKey = computed(() => {
  const categorySlug =
    store.state.NewCategory.selectedCategory?.slug ||
    route.params.categorySlug ||
    null;
  const subcategoryId =
    store.state.NewCategory.selectedSubcategory?.id ||
    route.params.subcategoryId ||
    null;

  // Combine categorySlug and subcategoryId for a unique key
  return subcategoryId ? `${categorySlug}-${subcategoryId}` : null;
});

// Add this computed property
const pageTitle = computed(() => {
  const subcategory = store.state.NewCategory.subcategories.find(
    (s) => s.id === parseInt(route.params.subcategoryId)
  );
  return `Choose a type of ${subcategory?.title || "item"}`;
});

onMounted(() => {
  const subcategorySlug = route.params.subcategorySlug;
  const categorySlug = route.query.category;

  console.log("subcategorySlug from route:", subcategorySlug);
  console.log("categorySlug from query:", categorySlug);

  if (!subcategorySlug) {
    console.error(
      "No subcategorySlug provided. Redirecting to PickSubCategory."
    );
    router.push({ name: "PickSubCategory" });
    return;
  }

  // Helper to slugify a string (if needed)
  const slugify = (text) =>
    text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]+/g, "")
      .replace(/\-\-+/g, "-");

  // Try to find the subcategory by slug or by slugified title
  const subcategories = store.state.NewCategory.subcategories;
  let subcategory = subcategories.find((s) => s.slug === subcategorySlug);

  if (!subcategory) {
    subcategory = subcategories.find(
      (s) => slugify(s.title) === subcategorySlug
    );
  }

  if (!subcategory) {
    // Subcategory not found - attempt to load subcategories first using categorySlug
    if (categorySlug) {
      const categories = store.state.NewCategory.categories;
      const category = categories.find((c) => c.slug === categorySlug);

      if (category) {
        store
          .dispatch("NewCategory/fetchSubCategories", category.id)
          .then(() => {
            const newSubcategories = store.state.NewCategory.subcategories;
            let newSubcategory = newSubcategories.find(
              (s) => s.slug === subcategorySlug
            );

            if (!newSubcategory) {
              newSubcategory = newSubcategories.find(
                (s) => slugify(s.title) === subcategorySlug
              );
            }

            if (newSubcategory) {
              // Now fetch groups for the newly loaded subcategory
              store.dispatch("NewCategory/fetchGroups", newSubcategory.id);
            } else {
              console.error(
                "Subcategory not found after fetching subcategories."
              );
              router.push({ name: "PickSubCategory" });
            }
          });
      } else {
        console.error("Category not found for slug:", categorySlug);
        router.push({ name: "PickSubCategory" });
      }
    } else {
      console.error(
        "No categorySlug provided in query, cannot load subcategories."
      );
      router.push({ name: "PickSubCategory" });
    }

    return;
  }

  // If subcategory found, fetch groups immediately
  store.dispatch("NewCategory/fetchGroups", subcategory.id);
});

const slugify = (text) =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-");
const navigateToSections = (group) => {
  console.log("Navigating to group:", group);

  // Track the selected group in Vuex
  store.dispatch("NewCategory/selectGroup", group);

  // Try to get values from Vuex store first
  let selectedCategory = store.getters["NewCategory/selectedCategory"];
  let selectedSubcategory = store.getters["NewCategory/selectedSubCategory"];

  // Debug current values
  console.log("From store - selectedCategory:", selectedCategory);
  console.log("From store - selectedSubcategory:", selectedSubcategory);

  // FALLBACK: If values not in store, try to get from route
  if (!selectedCategory || !selectedCategory.slug) {
    const categorySlugFromRoute = route.query.category;
    if (categorySlugFromRoute) {
      selectedCategory = store.state.NewCategory.categories.find(
        (c) => c.slug === categorySlugFromRoute
      );
      console.log("Fallback - found category from route:", selectedCategory);
    }
  }

  if (!selectedSubcategory || !selectedSubcategory.slug) {
    const subcategorySlugFromRoute = route.params.subcategorySlug;
    if (subcategorySlugFromRoute) {
      selectedSubcategory = store.state.NewCategory.subcategories.find(
        (s) =>
          s.slug === subcategorySlugFromRoute ||
          slugify(s.title || "") === subcategorySlugFromRoute
      );
      console.log(
        "Fallback - found subcategory from route:",
        selectedSubcategory
      );
    }
  }

  // Generate slugs with multiple fallback strategies
  const categorySlug =
    selectedCategory?.slug ||
    slugify(selectedCategory?.name || "") ||
    route.query.category ||
    "category";

  const subcategorySlug =
    selectedSubcategory?.slug ||
    slugify(selectedSubcategory?.title || "") ||
    route.params.subcategorySlug ||
    "subcategory";

  const groupSlug =
    group.slug || slugify(group.title || "") || `group-${group.id}`;

  console.log("Final slugs:", { categorySlug, subcategorySlug, groupSlug });

  // if (groupSlug === "information-technology" || subcategorySlug === "engineering") {
  //   router.push({
  //     name: "GeneralGroupsListComponent",
  //     params: { subcategorySlug },
  //     query: { category: categorySlug },
  //   });
  //   return;
  // }

  // Now navigate with the slugs
  if (group.sections && group.sections.length > 0) {
    // Navigate to GeneralSectionsListComponent with slugs and query params
    router.push({
      name: "GeneralSectionsListComponentForJob",
      params: { groupSlug },
      query: {
        category: categorySlug,
        subcategory: subcategorySlug,
      },
    });
  } else {
    // Navigate directly to GeneralListingSignUpComponent with all slugs as params
    router.push({
      name: "GeneralListingSignUpForJob",
      params: {
        categorySlug,
        subcategorySlug,
        groupSlug,
      },
    });
  }
};
</script>
<style scoped>
/* General Page Styling */
.subcategory-page {
  max-width: 50%;
  margin: 0 auto;
  padding: 16px;
  background-color: var(--white-pearl); /* Using CSS variable from white theme */
  min-height: 100vh; /* Ensure it fills the viewport height */
}

@media (max-width: 1024px) {
  .subcategory-page {
    max-width: 75%;
  }
}

@media (max-width: 768px) {
  .subcategory-page {
    max-width: 90%;
  }
}

/* Mobile Styles - Wider but not completely full-width */
@media (max-width: 600px) {
  .subcategory-page {
    max-width:100%;
    padding: 6px 4px;
  }
  
  /* Reduce container padding */
  .subcategory-container {
    padding: 6px 2px;
  }
}

/* Subcategory Container */
.subcategory-container {
  padding: 12px;
}

/* Subcategory Card Styling */
.subcategory-card {
 cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding: 12px; /* Adjust padding to reduce card size */
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: white;
  border: none;
  background: white;
  border: none !important;
  outline: none !important;
  box-shadow: none !important; /* Optional: remove all shadows */
  width: 100%;
}

.subcategory-card:hover {
  border-color: rgba(0, 0, 0, 0.4);
}

.subcategory-card:hover .subcategory-name {
  color: darkgoldenrod !important;
}

/* Card Content */
.card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

/* Subcategory Name */
.subcategory-name {
  font-weight: bold;
  font-size: 16px;
  letter-spacing: 0.5px;
  /* Handle text overflow */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  padding-right: 12px; /* Space between text and icon */
}

/* Arrow Icon */
.arrow-icon {
  color: #757575;
  flex-shrink: 0; /* Prevent icon from shrinking */
}

/* Breadcrumb Styling */
.breadcrumbs {
  padding: 8px 0;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
}

.breadcrumbs .v-breadcrumbs-item {
  font-weight: bold;
  color: #333;
}

.breadcrumbs .v-breadcrumbs-item:hover {
  color: darkgoldenrod;
}

.breadcrumbs .v-breadcrumbs-divider {
  color: #757575;
}

/* Mobile Adjustments for Long Text */
@media (max-width: 480px) {
  /* Allow text to wrap on very small screens */
  .subcategory-name {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.3;
  }
  
  /* Slightly smaller font size on mobile */
  .subcategory-name {
    font-size: 15px;
  }
  
  /* Adjust card padding for mobile */
  .subcategory-card {
    padding: 10px;
  }
}
</style>
